import { Box, Text } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { StepProgressionCondtion } from 'modules/processes/components/ProcessSidePanel'
import { StepStatuses } from 'modules/processes/domain'

import { useLocationTimezone } from './useLocationTimezone'

const getResourceLabel = (type: string) => {
  switch (type) {
    case 'contract_end_date':
      return i18n.t('processes.process_settings.steps.starts.by_date.resource.contract_end_date')
    case 'contract_start_date':
      return i18n.t('processes.process_settings.steps.starts.by_date.resource.contract_start_date')
    case 'contract_trial_end_date':
      return i18n.t(
        'processes.process_settings.steps.starts.by_date.resource.contract_trial_end_date'
      )
    case 'employee_termination_date':
      return i18n.t(
        'processes.process_settings.steps.starts.by_date.resource.employee_termination_date'
      )
    case 'process_start_date':
      return i18n.t('processes.process_settings.steps.starts.by_date.resource.process_start_date')
    case 'step_start_date':
      return i18n.t('processes.process_settings.steps.starts.by_date.resource.step_start_date')
    case 'training_completion_date':
      return i18n.t(
        'processes.process_settings.steps.starts.by_date.resource.training_completion_date'
      )
    default:
      throw new Error(`Unknown translation for type: ${type}`)
  }
}

interface Props {
  stepProgressionCondition: string
  stepMaterializationDate: string
  stepStatus?: string
  previousStepName?: string
  materializedStepCreatedAt?: string
  field?: string | null
  stepRemoved?: boolean
}

const TRANSLATIONS_BY_TYPE = {
  manual: () => i18n.t('processes.process_settings.steps.materialization.manual.incoming'),
  automatic: (previous_step_name: string) =>
    i18n.t('processes.process_settings.steps.materialization.automatic.incoming_step', {
      previous_step_name: previous_step_name,
    }),
  byDate: (daysUntilStart: number) =>
    i18n.tp('processes.process_settings.steps.materialization.by_date.incoming', {
      days_count: daysUntilStart,
      count: daysUntilStart,
    }),
}

const TRANSLATIONS_BY_STATUS = {
  finished: () => i18n.t('processes.process_settings.steps.materialization.finished'),
  finishedByDate: (materializationDate: moment.Moment) =>
    i18n.t('processes.process_settings.steps.materialization.finish_with_date', {
      date: materializationDate.format('LL'),
    }),
  inProgress: () => i18n.t('processes.process_settings.steps.materialization.in_progress'),
  inProgressByDate: (materializationDate: moment.Moment) =>
    i18n.t('processes.process_settings.steps.materialization.in_progress_with_date', {
      date: materializationDate.format('LL'),
    }),
}

export const MaterializedStepDescription = ({
  stepProgressionCondition,
  stepMaterializationDate,
  stepStatus,
  previousStepName,
  materializedStepCreatedAt,
  field,
  stepRemoved,
}: Props) => {
  const currentLocationId = useCurrentSession().employee?.locationId
  const { timezone } = useLocationTimezone(currentLocationId)

  const materializationDate = materializedStepCreatedAt
    ? moment(materializedStepCreatedAt).tz(String(timezone))
    : moment(stepMaterializationDate).startOf('day')

  const today = moment().tz(String(timezone))

  const daysUntilStart = Math.ceil(materializationDate.diff(today, 'hours') / 24)

  const isInProgress = stepStatus === StepStatuses.Running
  const isFinished = stepStatus === StepStatuses.Completed

  const inProgressDescription = (): string => {
    if (materializationDate.isValid()) {
      return TRANSLATIONS_BY_STATUS.inProgressByDate(materializationDate)
    }

    return TRANSLATIONS_BY_STATUS.inProgress()
  }

  const finishedDescription = (): string => {
    if (stepProgressionCondition === StepProgressionCondtion.ByDate) {
      return TRANSLATIONS_BY_STATUS.finishedByDate(materializationDate)
    }

    return TRANSLATIONS_BY_STATUS.finished()
  }

  const attributeNotFoundDescription = () => {
    return (
      <Text size="200" color="radicalRed1000" capitalize>
        {i18n.t('processes.process_settings.steps.materialization.by_date.missing', {
          field: getResourceLabel(field ?? ''),
        })}
      </Text>
    )
  }

  const description = (): string | JSX.Element => {
    if (isInProgress) return inProgressDescription()
    if (isFinished) return finishedDescription()

    switch (stepProgressionCondition) {
      case StepProgressionCondtion.Manual:
        return TRANSLATIONS_BY_TYPE.manual()
      case StepProgressionCondtion.Automatic:
        return TRANSLATIONS_BY_TYPE.automatic(String(previousStepName))
      case StepProgressionCondtion.ByDate:
        if (isNaN(daysUntilStart)) return attributeNotFoundDescription()

        return TRANSLATIONS_BY_TYPE.byDate(daysUntilStart)
      default:
        return ''
    }
  }

  return (
    <Box gap="s8">
      <Text size="200">{description()}</Text>
      {stepRemoved && (
        <Text size="200" color="radicalRed1000">
          {i18n.t('processes.process_settings.steps.materialization.position_error')}
        </Text>
      )}
    </Box>
  )
}
